import { Input, Autocomplete, AutocompleteItem, Button } from '@heroui/react';
import { Copy, Trash2 } from 'lucide-react';
import { resolveRailLength, railLengthLink } from '@estimator/shared';
import type { MaterialLibrary, BanquetteRail, ComponentDims } from '@estimator/shared';
import PanelFinishControls from '../shared/PanelFinishControls';
import PanelDimControls from '../shared/PanelDimControls';
import BoardRunControl from '../shared/BoardRunControl';

interface BanquetteRailRowProps {
  rail: BanquetteRail;
  materials: MaterialLibrary[];
  onChange: (updates: Partial<BanquetteRail>) => void;
  onRemove: () => void;
  /** Copy this row's material to the clipboard. Button hidden when absent. */
  onCopyMaterial?: () => void;
  /** Name of the template-level default profile, for the override popover label. */
  defaultFinishName?: string | null;
  /** Live and base dimensions, for resolving the length a legacy rail displays. */
  dims: ComponentDims;
  /**
   * Label the two numeric fields Width and Length. Off by default: the row's
   * two boxes read left-to-right as W then L, and the cushion headings already
   * say "Width Panels". Neither dimension carries a role — either may be
   * static or linked to any component axis.
   */
  showFieldLabels?: boolean;
}

export default function BanquetteRailRow({ rail, materials, onChange, onRemove, onCopyMaterial, showFieldLabels, defaultFinishName, dims }: BanquetteRailRowProps) {
  // A rail with no stored length reads as linked to the run axis, so the row
  // shows two explicit values and nothing reveals that the value is absent.
  const length = resolveRailLength(rail, dims).value;
  const lengthLink = railLengthLink(rail, dims);
  return (
    <div className="flex items-center gap-2">
      <Input
        label={showFieldLabels ? 'Name' : undefined}
        value={rail.name}
        onValueChange={(v) => onChange({ name: v })}
        size="sm"
        placeholder="Rail name"
        className="flex-1"
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
      />
      <Input
        type="number"
        label={showFieldLabels ? 'Width' : undefined}
        value={rail.width.toString()}
        onValueChange={(v) => onChange({ width: Number(v) || 0 })}
        size="sm"
        className={showFieldLabels ? 'w-24' : 'w-20'}
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
        endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
      />
      <PanelDimControls
        link={rail.width_link ?? null}
        onChange={(width_link) => onChange({ width_link })}
        label="Width"
      />
      <Input
        type="number"
        label={showFieldLabels ? 'Length' : undefined}
        value={length.toString()}
        onValueChange={(v) => onChange({ length: Number(v) || 0, length_link: lengthLink })}
        size="sm"
        className={showFieldLabels ? 'w-24' : 'w-20'}
        classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
        endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
      />
      <PanelDimControls
        link={lengthLink}
        onChange={(length_link) => onChange({ length, length_link })}
        label="Length"
      />
      <Autocomplete
        label={showFieldLabels ? 'Material' : undefined}
        selectedKey={rail.material_id ?? undefined}
        onSelectionChange={(key) => onChange({ material_id: (key as string) || null })}
        size="sm"
        placeholder="Material"
        className="flex-1"
        inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
      >
        {materials.map((m) => (
          <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
        ))}
      </Autocomplete>
      <BoardRunControl
        widthIn={rail.width ?? 0}
        lengthIn={length}
        material={materials.find((m) => m.id === rail.material_id)}
        flipped={rail.board_run_flipped ?? false}
        onChange={(board_run_flipped) => onChange({ board_run_flipped })}
      />
      <PanelFinishControls
        finish={rail.finish}
        onChange={(updates) => onChange({ finish: { ...rail.finish, ...updates } })}
        defaultFinishName={defaultFinishName ?? null}
      />
      {onCopyMaterial && (
        <Button
          isIconOnly
          size="sm"
          variant="light"
          aria-label="Copy material"
          title="Copy this material"
          isDisabled={!rail.material_id}
          onPress={onCopyMaterial}
        >
          <Copy size={14} style={{ color: 'var(--text-muted)' }} />
        </Button>
      )}
      <Button isIconOnly size="sm" variant="light" onPress={onRemove}>
        <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
      </Button>
    </div>
  );
}
